import { ref } from 'vue';
import { useAlertStore, useCommonStore, useUserStore } from '@stores';
import { AuthAdapter, AxiosAdapter } from '@adapters';
import {
  SignInAdminUseCase,
  SignInAutoUseCase,
  SignInUseCase,
} from '@core/useCases';
import { SNS_KIND } from '@core/enums';
import { useKakao, useNaver } from '@composables';

interface SigninPayload {
  isAdmin?: boolean;
}
const LOCAL_STORAGE_KEY_AUTO_LOGIN = '___autoLogin___';
const LOCAL_STORAGE_KEY_REFRESH_TOKEN = '___refreshToken___';
const SNS_SIGNIN = '___SNS_SIGNIN___';
const SNS_ACCESS_TOKEN = '___snsAccessToken___';

export const useSignIn = () => {
  const userStore = useUserStore();
  const commonStore = useCommonStore();
  const alertStore = useAlertStore();
  const { requestAuthUrl: requestAuthKakaoUrl } = useKakao();
  const { requestAuthUrl: requestAuthNaverUrl } = useNaver();

  const axiosAdapter = new AxiosAdapter();
  const authAdapter = new AuthAdapter(axiosAdapter);

  const idHpNo = ref<string>(commonStore.getLocalStorage('saveId') || '');
  const password = ref<string>('');
  const saveIdCheck = ref(!!idHpNo.value);
  const autoLoginFlag = ref<boolean>(false);
  const error = ref<string | null>(null);
  const loading = ref<boolean>(false);

  const _signIn = async () => {
    const signInUseCase = new SignInUseCase(authAdapter);
    return signInUseCase.execute({
      idHpNo: idHpNo.value,
      password: password.value,
      autoFlag: autoLoginFlag.value,
    });
  };

  const _signInAdmin = async () => {
    const signInAdminUseCase = new SignInAdminUseCase(authAdapter);
    return signInAdminUseCase.execute({
      email: idHpNo.value,
      password: password.value,
    });
  };

  const signInAuto = async (token: string) => {
    const signInAutoUseCase = new SignInAutoUseCase(authAdapter);
    await signInAutoUseCase.execute({
      token: token,
    });
  };

  const _handleSaveId = () => {
    saveIdCheck.value
      ? commonStore.setStorage('localStorage', 'saveId', idHpNo.value)
      : commonStore.removeStorage('localStorage', 'saveId');
  };
  const _handleAutoLogin = (refreshToken?: string) => {
    if (autoLoginFlag.value) {
      commonStore.setStorage(
        'localStorage',
        LOCAL_STORAGE_KEY_AUTO_LOGIN,
        String(autoLoginFlag.value)
      );
      commonStore.setStorage(
        'localStorage',
        LOCAL_STORAGE_KEY_REFRESH_TOKEN,
        refreshToken!
      );
    } else {
      commonStore.removeStorage('localStorage', LOCAL_STORAGE_KEY_AUTO_LOGIN);
      commonStore.removeStorage(
        'localStorage',
        LOCAL_STORAGE_KEY_REFRESH_TOKEN
      );
    }
  };

  const _showWelcomeMessage = async () => {
    const userName = userStore.username;
    await alertStore.showAlertDialog(`${userName} 회원님 환영합니다.`);
  };
  const handleSnsSignIn = (snsKind: SNS_KIND) => {
    if (snsKind === SNS_KIND.KAKAO) {
      window.location.href = requestAuthKakaoUrl;
    }
    if (snsKind === SNS_KIND.NAVER) {
      window.location.href = requestAuthNaverUrl;
    }
  };

  const handleSignIn = async (payload: SigninPayload = {}) => {
    loading.value = true;
    error.value = null;
    const { isAdmin } = payload;

    if (!idHpNo.value) {
      await alertStore.showAlertDialog('아이디를 입력해주세요.');
      return;
    }

    if (!password.value) {
      await alertStore.showAlertDialog('비밀번호를 입력해주세요.');
      return;
    }

    try {
      const signInResult = await (isAdmin ? _signInAdmin() : _signIn());
      if (commonStore.isSignIn) {
        _handleSaveId();
        if (!isAdmin) {
          _handleAutoLogin(signInResult?.refreshToken);
        }
        await _showWelcomeMessage();
        userStore.username && userStore.setMemberName(userStore.username);
        return true;
      }
      return;
    } catch (err: any) {
      error.value = err.message || '로그인에 실패했습니다.';
    } finally {
      loading.value = false;
    }
  };

  return {
    handleSnsSignIn,
    SNS_SIGNIN,
    SNS_ACCESS_TOKEN,

    idHpNo,
    password,
    saveIdCheck,
    autoLoginFlag,
    handleSignIn,
    signInAuto,
    error,
    loading,
  };
};
